import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import AuthModal from '../auth/AuthModal';
import './PricingPage.css';

const PricingPage = () => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [billingInterval, setBillingInterval] = useState('monthly'); // 'monthly' or 'annual'
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalMode, setAuthModalMode] = useState('signup');
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchPricing();
  }, []);

  // Check for pending subscription after user logs in
  useEffect(() => {
    console.log('💳 PRICING: useEffect triggered', { isAuthenticated, hasPricing: !!pricing });

    if (isAuthenticated && pricing) {
      const pendingSubscription = localStorage.getItem('pendingSubscription');
      console.log('💳 PRICING: Checking for pending subscription:', pendingSubscription);

      if (pendingSubscription) {
        try {
          const { priceId, tierName } = JSON.parse(pendingSubscription);
          console.log('💳 PRICING: Found pending subscription, processing:', { priceId, tierName });
          localStorage.removeItem('pendingSubscription');
          // Automatically trigger the subscription
          handleSubscribe(priceId, tierName);
        } catch (error) {
          console.error('💳 PRICING: Error processing pending subscription:', error);
          localStorage.removeItem('pendingSubscription');
        }
      }
    }
  }, [isAuthenticated, pricing]);

  const fetchPricing = async () => {
    try {
      const response = await fetch('/api/subscriptions/pricing');
      const data = await response.json();
      setPricing(data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (priceId, tierName) => {
    console.log('💳 PRICING v4: handleSubscribe called', { priceId, tierName, isAuthenticated });

    if (!isAuthenticated) {
      // Store the intended subscription in localStorage
      const pendingSub = { priceId, tierName };
      localStorage.setItem('pendingSubscription', JSON.stringify(pendingSub));
      console.log('💳 PRICING v4: Stored pending subscription:', pendingSub);
      console.log('💳 PRICING v4: Opening auth modal on pricing page (not navigating away)');

      // Open the auth modal right here on the pricing page
      setAuthModalMode('signup');
      setShowAuthModal(true);
      return;
    }

    console.log('💳 PRICING v4: User is authenticated, proceeding with checkout');

    try {
      const token = localStorage.getItem('authToken');
      console.log('💳 PRICING v4: Retrieved auth token:', token ? 'Token exists' : 'NO TOKEN FOUND');

      if (!token) {
        console.error('💳 PRICING v4: No auth token found in localStorage');
        alert('Authentication error. Please sign in again.');
        return;
      }

      console.log('💳 PRICING v4: Creating checkout session for priceId:', priceId);
      const response = await fetch('/api/subscriptions/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ priceId })
      });

      console.log('💳 PRICING v4: Checkout session response status:', response.status);
      const data = await response.json();
      console.log('💳 PRICING v4: Checkout session response data:', data);

      if (!response.ok) {
        console.error('💳 PRICING v4: Checkout session creation failed');
        console.error('💳 PRICING v4: Error response:', data);
        const errorMessage = data.error || data.message || 'Failed to start checkout';
        alert(`Checkout failed: ${errorMessage}\n\nPlease check the console for details or contact support.`);
        return;
      }

      if (data.url) {
        console.log('💳 PRICING v4: Redirecting to Stripe Checkout:', data.url);
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        console.error('💳 PRICING v4: No checkout URL in response:', data);
        alert(data.error || data.message || 'Failed to start checkout. Please try again.');
      }
    } catch (error) {
      console.error('💳 PRICING v4: Error creating checkout session:', error);
      alert(`Failed to start checkout: ${error.message}\n\nPlease check the console for details.`);
    }
  };

  const PricingCard = ({ tier, isPopular }) => {
    const isFree = tier.id === 'free';
    const price = billingInterval === 'monthly' ? tier.monthlyPrice : tier.annualPrice;
    const priceId = billingInterval === 'monthly' ? tier.monthlyPriceId : tier.annualPriceId;
    const savings = tier.annualPrice ? Math.round((1 - (tier.annualPrice / 12) / tier.monthlyPrice) * 100) : 0;

    const userTier = user?.subscription?.tier || 'free';
    const isCurrentPlan = userTier === tier.id;

    return (
      <div className={`pricing-card ${isPopular ? 'popular' : ''} ${isCurrentPlan ? 'current-plan' : ''}`}>
        {isPopular && <div className="popular-badge">Most Popular</div>}
        {isCurrentPlan && <div className="current-plan-badge">Current Plan</div>}

        <div className="pricing-card-header">
          <h3>{tier.name}</h3>

          {isFree ? (
            <div className="price">
              <span className="price-amount">$0</span>
              <span className="price-interval">/month</span>
            </div>
          ) : (
            <>
              <div className="price">
                <span className="price-currency">$</span>
                <span className="price-amount">{price}</span>
                <span className="price-interval">/{billingInterval === 'monthly' ? 'mo' : 'yr'}</span>
              </div>
              {billingInterval === 'annual' && savings > 0 && (
                <div className="savings-badge">Save {savings}%</div>
              )}
              {tier.trial && (
                <div className="trial-badge">{tier.trial}</div>
              )}
            </>
          )}
        </div>

        <ul className="features-list">
          {tier.features.map((feature, index) => (
            <li key={index}>
              <svg className="checkmark" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              {feature}
            </li>
          ))}
        </ul>

        <button
          className={`pricing-cta ${isCurrentPlan ? 'current' : ''}`}
          onClick={() => !isFree && !isCurrentPlan && handleSubscribe(priceId, tier.name)}
          disabled={isCurrentPlan}
        >
          {isCurrentPlan ? 'Current Plan' : isFree ? 'Get Started Free' : `Start ${tier.trial || 'Free Trial'}`}
        </button>

        {!isFree && !isCurrentPlan && (
          <p className="no-credit-card">
            {tier.id === 'plus' ? 'No credit card required for first 7 days' : '30-day money-back guarantee'}
          </p>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="pricing-page loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="pricing-page">
      <div className="pricing-container">
        <div className="pricing-header">
          <h1>Travel Safely. Explore Confidently.</h1>
          <p className="pricing-subtitle">
            Choose the plan that's right for you. All plans include our core safety features.
          </p>

          {/* Billing interval toggle */}
          {pricing?.tiers.some(t => t.annualPrice) && (
            <div className="billing-toggle">
              <button
                className={billingInterval === 'monthly' ? 'active' : ''}
                onClick={() => setBillingInterval('monthly')}
              >
                Monthly
              </button>
              <button
                className={billingInterval === 'annual' ? 'active' : ''}
                onClick={() => setBillingInterval('annual')}
              >
                Annual
                <span className="toggle-badge">Save 25%</span>
              </button>
            </div>
          )}
        </div>

        <div className="pricing-grid">
          {pricing?.tiers.map((tier) => (
            <PricingCard
              key={tier.id}
              tier={tier}
              isPopular={tier.popular}
            />
          ))}
        </div>

        <div className="pricing-faq">
          <h2>Frequently Asked Questions</h2>

          <div className="faq-item">
            <h3>Can I cancel anytime?</h3>
            <p>
              Yes, absolutely. Cancel anytime from your account settings. No phone calls, no hassle.
              If you cancel a monthly subscription, you'll keep access until the end of your paid period.
              Annual subscriptions come with a 30-day money-back guarantee.
            </p>
          </div>

          <div className="faq-item">
            <h3>What happens to my reviews and data if I cancel?</h3>
            <p>
              Your reviews and contributions remain on WanderVibe (they help other travelers stay safe).
              You can always reactivate your account and access your data. You can request full data
              deletion by contacting support@wandervibe.app.
            </p>
          </div>

          <div className="faq-item">
            <h3>What payment methods do you accept?</h3>
            <p>
              We accept all major credit cards (Visa, Mastercard, American Express, Discover) and debit cards.
              Payments are securely processed by Stripe. We do not store your card information.
            </p>
          </div>

          <div className="faq-item">
            <h3>Can I upgrade or downgrade my plan?</h3>
            <p>
              Yes! Upgrade anytime and start using new features immediately. If you downgrade, the change
              takes effect at the end of your current billing period, so you never lose access early.
            </p>
          </div>

          <div className="faq-item">
            <h3>Is my payment information secure?</h3>
            <p>
              Absolutely. We use Stripe, a PCI-compliant payment processor trusted by millions of businesses.
              We never see or store your full card details.
            </p>
          </div>
        </div>
      </div>

      {/* Auth Modal for signup/login directly on pricing page */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        defaultMode={authModalMode}
        onAuthSuccess={() => {
          console.log('💳 PRICING v4: Auth successful, closing modal');
          setShowAuthModal(false);
          // The useEffect will handle processing the pending subscription
        }}
      />
    </div>
  );
};

export default PricingPage;
